import { JSX, ParentProps, Show, createMemo } from 'solid-js'
import styles from './st-draggable.module.css'
import Helper from './fragments/Helper';
import Content from './fragments/Content';
import Handle from './fragments/Handle';
import { DraggableContext, DraggableContextJsx } from './DraggableContext';
import { getDropTargetRegistry, type DropItem } from '../st-drop-target/st-drop-target';

export class DraggableProps<T> {
    disabled?: boolean = false;
    floating?: boolean = false;
    clone?: boolean = false;
    targets?: string;                  // Comma-separated IDs of accepted drop zones
    item?: T = {} as T;
    id?: string;                       // ID for this draggable (for accepts matching)

    // Drop-related callbacks
    onDragStart?: (item: T) => void;
    onDragEnd?: (item: T) => void;
    dragData?: any;  // Additional data to pass during drag
}

export const dragChannel = new BroadcastChannel('st.drag');

type DragState = {
    context: DraggableContext;
    originalRect: DOMRect;
    originalOffset: { x: number, y: number },
    originalMousePos: { x: number, y: number },
    el: HTMLElement;
}

let dragState: DragState | undefined;
let currentHoverTarget: HTMLElement | null = null;

export default function Draggable<T>(props: ParentProps<DraggableProps<T>> & Omit<JSX.HTMLAttributes<HTMLElement>, 'onDragStart' | 'onDragEnd' | 'onDrop'>) {
    const ctx = new DraggableContext();
    ctx.floating = props.floating ?? false;
    ctx.cloneHelper = props.clone ?? false;

    const className = createMemo(() => {
        const res = [styles.draggable];

        if (ctx.floating) {
            res.push(styles.floating);
        }

        if (!ctx.hasHandle()) {
            res.push(styles.handle);
        }

        return res.join(' ');
    });

    let canDrag = true;
    let mainDiv!: HTMLDivElement;

    function onMouseDown(this: HTMLElement, ev: MouseEvent) {
        const target = (ev.target as HTMLElement);

        canDrag = false;
        if (target && !props.disabled) {
            if (target.closest(`.${styles.draggable}`) === mainDiv) {
                const handle = target.closest(`.${styles.handle}`);
                if (ctx.hasHandle()) {
                    canDrag = mainDiv !== handle && mainDiv.contains(handle);
                }
                else {
                    canDrag = mainDiv === handle;
                }
            }
        }

        if (canDrag) {
            const rect = mainDiv.getBoundingClientRect();

            // Store original position style and computed position
            const originalPosition = mainDiv.style.position;
            const computedStyle = window.getComputedStyle(mainDiv);
            const isAlreadyAbsolute = computedStyle.position === 'absolute';

            // Only change position for non-floating elements
            if (!ctx.floating) {
                mainDiv.style.position = 'absolute';
                mainDiv.style.top = `${mainDiv.offsetTop}px`;
                mainDiv.style.left = `${mainDiv.offsetLeft}px`;
            }

            // Store props for later access in event handlers
            (mainDiv as any).__draggableProps = props;
            (mainDiv as any).__originalPosition = originalPosition;

            dragState = {
                context: ctx,
                el: mainDiv,
                originalOffset: {
                    x: mainDiv.offsetLeft,
                    y: mainDiv.offsetTop
                },
                originalRect: rect,
                originalMousePos: {
                    x: ev.pageX,
                    y: ev.pageY,
                },
            }
        }
    }

    return (
        <DraggableContextJsx.Provider value={ctx}>
            <div ref={mainDiv} class={className()} onMouseDown={onMouseDown} >
                {props.children}
            </div>
            <Show when={ctx.cloneHelper}>
                <Draggable.Helper>
                    {props.children}
                </Draggable.Helper>
            </Show>
        </DraggableContextJsx.Provider>
    );
}

Draggable.Helper = Helper;
Draggable.Content = Content;
Draggable.Handle = Handle;

Draggable.defaultProps = new DraggableProps();

const blankImage = new Image();
blankImage.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAApJREFUCNdjYAAAAAIAAeIhvDMAAAAASUVORK5CYII=';

// Register event listeners immediately when module loads - works with HMR
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.document.addEventListener('mousemove', ev => {
        if (dragState) {
            const el = dragState.el;
            const ctx = dragState.context;
            const mpos = dragState.originalMousePos;

            if (Math.abs(mpos.x - ev.pageX) > 5
                || Math.abs(mpos.y - ev.pageY) > 5
                || ctx.isDragging()) {

                const wasDragging = ctx.isDragging();
                ctx.setIsDragging(true);

                // Fire onDragStart callback on first drag movement
                if (!wasDragging) {
                    const props = (el as any).__draggableProps;
                    props?.onDragStart?.(props.item);

                    // Broadcast via channel
                    try {
                        dragChannel.postMessage({
                            type: 'dragstart',
                            item: props?.item,
                            data: props?.dragData
                        });
                    } catch (e) {
                        // Ignore DataCloneError for non-serializable objects
                    }
                }

                // Detect drop targets using registry
                const props = (el as any).__draggableProps;
                const registry = getDropTargetRegistry();
                const elementsUnderCursor = document.elementsFromPoint(ev.clientX, ev.clientY);

                let validTarget: HTMLElement | null = null;
                let validRegistration: any = null;

                // Find first registered drop target under cursor that accepts this draggable
                for (const [id, registration] of registry) {
                    if (registration.disabled) continue;

                    // Check if this element is under cursor
                    if (elementsUnderCursor.includes(registration.element)) {
                        // Check if this drop target accepts this draggable
                        const draggableId = props?.id || '';
                        if (registration.accepts.length === 0 || registration.accepts.includes(draggableId)) {
                            validTarget = registration.element;
                            validRegistration = registration;
                            break;
                        }
                    }
                }

                // Update hover state
                if (validTarget !== currentHoverTarget) {
                    // Clear previous hover
                    if (currentHoverTarget) {
                        const prevReg = Array.from(registry.values()).find(r => r.element === currentHoverTarget);
                        prevReg?.setIsHovered(false);
                    }

                    // Set new hover
                    if (validRegistration) {
                        validRegistration.setIsHovered(true);
                    }

                    currentHoverTarget = validTarget;
                }

                const helper = ctx.helperElement();
                if (helper) {
                    const x = dragState.originalRect.x + (ev.pageX - mpos.x);
                    const y = dragState.originalRect.y + (ev.pageY - mpos.y);

                    helper.style.position = 'absolute';
                    helper.style.left = `${x}px`;
                    helper.style.top = `${y}px`;
                }
                else {
                    const x = dragState.originalOffset.x + (ev.pageX - mpos.x);
                    const y = dragState.originalOffset.y + (ev.pageY - mpos.y);

                    el.style.left = `${x}px`;
                    el.style.top = `${y}px`;
                }
            }
        }
    });

    window.document.addEventListener('mouseup', ev => {
        if (dragState) {
            const el = dragState.el;
            const ctx = dragState.context;
            const rect = dragState.originalRect;
            const mpos = dragState.originalMousePos;
            const x = rect.left + (ev.pageX - mpos.x);
            const y = rect.top + (ev.pageY - mpos.y);

            const wasDragging = ctx.isDragging();
            ctx.setIsDragging(false);

            // Get props from element
            const props = (el as any).__draggableProps;

            // Fire onDragEnd callback
            if (wasDragging && props) {
                props.onDragEnd?.(props.item);

                // Fire onDrop if dropped on valid target
                if (currentHoverTarget) {
                    const registry = getDropTargetRegistry();
                    const registration = Array.from(registry.values()).find(r => r.element === currentHoverTarget);

                    if (registration?.onDrop) {
                        const dropItem: DropItem = {
                            id: props.id || '',
                            data: props.item
                        };
                        registration.onDrop(dropItem);
                    }

                    try {
                        dragChannel.postMessage({
                            type: 'drop',
                            item: props.item,
                            target: currentHoverTarget.id || currentHoverTarget.className
                        });
                    } catch (e) {
                        // Ignore DataCloneError
                    }
                }

                // Broadcast dragend
                try {
                    dragChannel.postMessage({
                        type: 'dragend',
                        item: props.item
                    });
                } catch (e) {
                    // Ignore DataCloneError - CRITICAL: must not block dragState cleanup
                }
            }

            // Cleanup hover state
            if (currentHoverTarget) {
                const registry = getDropTargetRegistry();
                const registration = Array.from(registry.values()).find(r => r.element === currentHoverTarget);
                registration?.setIsHovered(false);
                currentHoverTarget = null;
            }

            if (!ctx.floating) {
                // Restore original position for non-floating elements
                el.style.left = `${dragState.originalOffset.x}px`;
                el.style.top = `${dragState.originalOffset.y}px`;

                // Restore original position style
                const originalPosition = (el as any).__originalPosition;
                if (originalPosition) {
                    el.style.position = originalPosition;
                } else {
                    el.style.position = '';
                }
            }
        }

        dragState = undefined;
    });
}
